import React, { useContext, useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import AppModal from "../AppModal";
import TemplateConversationPreview from "../TemplateConversationPreview";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";
import { resolveUploadedFileUrl } from "../../helpers/resolveMediaUrl";

const CATEGORY_OPTIONS = [
	{ value: "MARKETING", label: "Marketing" },
	{ value: "UTILITY", label: "Utilidade" },
	{ value: "AUTHENTICATION", label: "Autenticação" },
];

const HEADER_FORMAT_OPTIONS = [
	{ value: "NONE", label: "Sem cabeçalho" },
	{ value: "TEXT", label: "Texto" },
	{ value: "IMAGE", label: "Imagem" },
	{ value: "VIDEO", label: "Vídeo" },
	{ value: "DOCUMENT", label: "Documento" },
];

const STEPS = [
	{ id: "setup", label: "Configuração" },
	{ id: "content", label: "Conteúdo" },
	{ id: "buttons", label: "Botões" },
	{ id: "review", label: "Revisar" },
];

const MEDIA_ACCEPT = {
	IMAGE: "image/jpeg,image/jpg,image/png",
	VIDEO: "video/mp4,video/3gpp",
	DOCUMENT: "application/pdf,.pdf",
};

const MEDIA_MIME_BY_FORMAT = {
	IMAGE: ["image/jpeg", "image/jpg", "image/png"],
	VIDEO: ["video/mp4", "video/3gpp"],
	DOCUMENT: ["application/pdf"],
};

function FieldLabel({ children, required }) {
	return (
		<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
			{children}
			{required ? <span className="ml-0.5 text-danger">*</span> : null}
		</label>
	);
}

function FieldHint({ children }) {
	return (
		<p className="mt-2 mb-0 text-xs text-default-500 dark:text-default-400">{children}</p>
	);
}

function FieldError({ children }) {
	if (!children) return null;
	return <p className="mt-1 mb-0 text-xs text-danger">{children}</p>;
}

function IconPaperclip() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.82-2.82l8.48-8.48" />
		</svg>
	);
}

function IconPlus() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 5v14M5 12h14" />
		</svg>
	);
}

function IconTrash() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
		</svg>
	);
}

function getInitialTemplate(selectedTemplate) {
	return {
		name: selectedTemplate?.name || "",
		header: selectedTemplate?.header || "",
		body: selectedTemplate?.body || "",
		footer: selectedTemplate?.footer || "",
	};
}

function buildTemplateComponents({
	headerFormat,
	header = "",
	body = "",
	footer = "",
	mediaPath,
	headerExamples = [],
	bodyExamples = [],
	buttons = [],
}) {
	const components = [];

	if (headerFormat === "TEXT" && String(header || "").trim()) {
		const headerComp = { type: "HEADER", format: "TEXT", text: header };
		if (headerExamples.length) {
			headerComp.example = { header_text: headerExamples };
		}
		components.push(headerComp);
	} else if (["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat)) {
		const headerComp = { type: "HEADER", format: headerFormat };
		if (mediaPath) {
			headerComp.example = { header_handle: [mediaPath] };
		}
		components.push(headerComp);
	}

	const bodyComp = { type: "BODY", text: body };
	if (bodyExamples.length) {
		// Meta exige body_text aninhado: [["ex1", "ex2"]]
		bodyComp.example = { body_text: [bodyExamples] };
	}
	components.push(bodyComp);

	if (String(footer || "").trim()) {
		components.push({ type: "FOOTER", text: footer });
	}

	if (buttons.length) {
		components.push({
			type: "BUTTONS",
			buttons: buttons.map(btn => {
				const item = {
					type: btn.type || "QUICK_REPLY",
					text: btn.text,
				};
				if (String(btn.type || "").toUpperCase() === "URL") {
					item.url = btn.url;
					if (btn.example) item.example = [btn.example];
				}
				return item;
			}),
		});
	}

	return components;
}

function listPlaceholderIndexes(text) {
	const matches = [...String(text || "").matchAll(/\{\{(\d+)\}\}/g)];
	if (!matches.length) return [];
	const max = matches.reduce((acc, match) => Math.max(acc, Number(match[1]) || 0), 0);
	return Array.from({ length: max }, (_, index) => index + 1);
}

function resizeExamples(current, count) {
	const next = Array.from({ length: count }, (_, index) => current[index] || "");
	return next;
}

function nextPlaceholderToken(text) {
	const indexes = listPlaceholderIndexes(text);
	const next = indexes.length ? Math.max(...indexes) + 1 : 1;
	return `{{${next}}}`;
}

function insertAtCursor(value, insertion, selectionStart, selectionEnd) {
	const start = selectionStart ?? value.length;
	const end = selectionEnd ?? value.length;
	return {
		value: `${value.slice(0, start)}${insertion}${value.slice(end)}`,
		cursor: start + insertion.length,
	};
}

function StepRail({ steps, activeIndex, onSelect }) {
	return (
		<ol className="mb-0 flex list-none flex-wrap items-center gap-2 p-0">
			{steps.map((step, index) => {
				const done = index < activeIndex;
				const active = index === activeIndex;
				return (
					<li key={step.id} className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => onSelect(index)}
							disabled={index > activeIndex}
							className={[
								"inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition",
								active
									? "bg-primary text-white shadow-sm"
									: done
										? "bg-primary/10 text-primary"
										: "bg-default-100 text-default-500 dark:bg-default-800 dark:text-default-400",
								index > activeIndex ? "cursor-not-allowed opacity-60" : "cursor-pointer",
							].join(" ")}
						>
							<span
								className={[
									"inline-flex size-5 items-center justify-center rounded-full text-[11px]",
									active
										? "bg-white/20"
										: done
											? "bg-primary text-white"
											: "bg-default-200 dark:bg-default-700",
								].join(" ")}
							>
								{done ? "✓" : index + 1}
							</span>
							{step.label}
						</button>
						{index < steps.length - 1 && (
							<span className="hidden h-px w-6 bg-default-200 sm:block dark:bg-default-700" />
						)}
					</li>
				);
			})}
		</ol>
	);
}

export default function TemplateModal({ open, onClose, selectedTemplate = {}, whatsApps = [] }) {
	const { darkMode } = useContext(ThemeContext);
	const [newTemplate, setNewTemplate] = useState(getInitialTemplate(selectedTemplate));
	const [error, setError] = useState("");
	const [selectedWhatsAppId, setSelectedWhatsAppId] = useState("");
	const [category, setCategory] = useState("UTILITY");
	const [headerFormat, setHeaderFormat] = useState("NONE");
	const [loading, setLoading] = useState(false);
	const [uploadingMedia, setUploadingMedia] = useState(false);
	const [mediaPath, setMediaPath] = useState(null);
	const [mediaHandle, setMediaHandle] = useState("");
	const [buttons, setButtons] = useState([]);
	const [headerExamples, setHeaderExamples] = useState([]);
	const [bodyExamples, setBodyExamples] = useState([]);
	const [stepIndex, setStepIndex] = useState(0);

	const isEditing = Boolean(selectedTemplate?.header);
	const isMediaHeader = ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat);

	const headerPlaceholderIndexes = useMemo(
		() => (headerFormat === "TEXT" ? listPlaceholderIndexes(newTemplate.header) : []),
		[headerFormat, newTemplate.header]
	);
	const bodyPlaceholderIndexes = useMemo(
		() => listPlaceholderIndexes(newTemplate.body),
		[newTemplate.body]
	);

	const eligibleWhatsApps = useMemo(
		() =>
			whatsApps.filter(whatsApp => {
				const channel = String(whatsApp?.channel || "").toLowerCase();
				return channel === "whatsapp_business_account";
			}),
		[whatsApps]
	);

	const selectedWhatsApp = useMemo(
		() =>
			eligibleWhatsApps.find(whatsApp => String(whatsApp.id) === String(selectedWhatsAppId)) ||
			null,
		[selectedWhatsAppId, eligibleWhatsApps]
	);

	const isHubConnection = Boolean(
		selectedWhatsApp && (selectedWhatsApp.hub === true || selectedWhatsApp.hub === 1)
	);

	const cleanButtons = useMemo(
		() =>
			buttons
				.map(btn => {
					const type = String(btn.type || "QUICK_REPLY").toUpperCase();
					const text = String(btn.text || "").trim();
					if (!text) return null;
					if (type === "URL") {
						const url = String(btn.url || "").trim();
						if (!url) return null;
						const item = { type: "URL", text, url };
						if (listPlaceholderIndexes(url).length) {
							item.example = String(btn.example || "").trim();
						}
						return item;
					}
					return { type: "QUICK_REPLY", text };
				})
				.filter(Boolean),
		[buttons]
	);

	const previewParams = useMemo(() => {
		const params = [];
		headerPlaceholderIndexes.forEach((_, index) => {
			params.push(headerExamples[index] || `{{${index + 1}}}`);
		});
		bodyPlaceholderIndexes.forEach((_, index) => {
			params.push(bodyExamples[index] || `{{${index + 1}}}`);
		});
		return params;
	}, [headerPlaceholderIndexes, bodyPlaceholderIndexes, headerExamples, bodyExamples]);

	const previewTemplate = useMemo(() => {
		const name = String(newTemplate.name || "").trim() || "novo_template";
		const components = [];

		if (headerFormat === "TEXT" && String(newTemplate.header || "").trim()) {
			components.push({
				type: "HEADER",
				format: "TEXT",
				text: newTemplate.header,
			});
		} else if (isMediaHeader) {
			components.push({ type: "HEADER", format: headerFormat });
		}

		components.push({
			type: "BODY",
			text: newTemplate.body || "Escreva o corpo da mensagem…",
		});

		if (String(newTemplate.footer || "").trim()) {
			components.push({ type: "FOOTER", text: newTemplate.footer });
		}

		if (cleanButtons.length) {
			components.push({
				type: "BUTTONS",
				buttons: cleanButtons.map(btn => ({
					type: btn.type,
					text: btn.text,
					url: btn.url,
				})),
			});
		}

		return { name, components };
	}, [newTemplate, headerFormat, isMediaHeader, cleanButtons]);

	useEffect(() => {
		setNewTemplate(getInitialTemplate(selectedTemplate));
		setError("");
		setMediaPath(null);
		setMediaHandle("");
		setCategory("UTILITY");
		setHeaderFormat("NONE");
		setSelectedWhatsAppId("");
		setButtons([]);
		setHeaderExamples([]);
		setBodyExamples([]);
		setStepIndex(0);
	}, [selectedTemplate, open]);

	useEffect(() => {
		setHeaderExamples(current => resizeExamples(current, headerPlaceholderIndexes.length));
	}, [headerPlaceholderIndexes.length]);

	useEffect(() => {
		setBodyExamples(current => resizeExamples(current, bodyPlaceholderIndexes.length));
	}, [bodyPlaceholderIndexes.length]);

	useEffect(() => {
		if (isEditing || !open) return;
		if (selectedWhatsAppId) return;
		if (eligibleWhatsApps.length === 1) {
			setSelectedWhatsAppId(String(eligibleWhatsApps[0].id));
		}
	}, [eligibleWhatsApps, isEditing, open, selectedWhatsAppId]);

	const handleClose = () => onClose();

	const handleUpdateMedia = async event => {
		if (!event.target.files?.length) return;

		const file = event.target.files[0];
		const lowerName = String(file.name || "").toLowerCase();
		const mime = String(file.type || "").toLowerCase();

		if (lowerName.endsWith(".svg") || mime === "image/svg+xml") {
			setError("SVG não é aceito pela Meta. Use JPEG ou PNG.");
			event.target.value = "";
			return;
		}

		const allowed = MEDIA_MIME_BY_FORMAT[headerFormat] || [];
		const extOk =
			(headerFormat === "IMAGE" && /\.(jpe?g|png)$/i.test(lowerName)) ||
			(headerFormat === "VIDEO" && /\.(mp4|3gp)$/i.test(lowerName)) ||
			(headerFormat === "DOCUMENT" && /\.pdf$/i.test(lowerName));
		const mimeOk = !mime || allowed.includes(mime) || (mime === "image/jpg" && headerFormat === "IMAGE");

		if (!extOk && !mimeOk) {
			setError(
				headerFormat === "IMAGE"
					? "Para imagem use apenas JPEG ou PNG."
					: headerFormat === "VIDEO"
						? "Para vídeo use apenas MP4 ou 3GP."
						: "Para documento use apenas PDF."
			);
			event.target.value = "";
			return;
		}

		const localPreview = URL.createObjectURL(file);

		try {
			setUploadingMedia(true);
			setError("");

			if (isHubConnection) {
				if (!selectedWhatsApp?.id) {
					setError("Selecione a conexão antes de anexar a mídia.");
					return;
				}

				const formData = new FormData();
				formData.append("whatsappId", String(selectedWhatsApp.id));
				formData.append("file", file);

				const { data } = await apiV2.post("/hub/templates/media", formData, {
					headers: { "Content-Type": "multipart/form-data" },
				});

				const handle = data?.handle || data?.id || "";
				if (!handle) {
					throw new Error("Upload Hub não retornou o handle da Meta.");
				}

				setMediaHandle(String(handle));
				setMediaPath(localPreview);
				return;
			}

			const formData = new FormData();
			formData.append("file", file);

			const response = await api.post("/chatbot/node-upload-image", formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			const url = resolveUploadedFileUrl(file, response);
			setMediaPath(url);
			setMediaHandle("");
		} catch (err) {
			setMediaPath(null);
			setMediaHandle("");
			setError(err?.response?.data?.error || err?.message || "Falha no upload da mídia.");
			toastError(err);
		} finally {
			setUploadingMedia(false);
			event.target.value = "";
		}
	};

	const insertVariable = field => {
		setNewTemplate(current => {
			const currentValue = current[field] || "";
			const token = nextPlaceholderToken(currentValue);
			const result = insertAtCursor(currentValue, token);
			return { ...current, [field]: result.value };
		});
	};

	const validateStep = index => {
		const stepId = STEPS[index]?.id;
		if (stepId === "setup") {
			if (!isEditing && !selectedWhatsApp) {
				setError("Selecione uma conexão.");
				return false;
			}
			if (!String(newTemplate.name || "").trim() && !isEditing) {
				setError("Informe o nome do template.");
				return false;
			}
		}
		if (stepId === "content") {
			if (!String(newTemplate.body || "").trim()) {
				setError("Informe o corpo do template.");
				return false;
			}
			if (headerFormat === "TEXT" && !String(newTemplate.header || "").trim()) {
				setError("Informe o texto do cabeçalho ou escolha outro tipo.");
				return false;
			}
			if (isMediaHeader && !mediaPath) {
				setError("Anexe um arquivo de exemplo para o cabeçalho de mídia.");
				return false;
			}
			if (isMediaHeader && isHubConnection && !String(mediaHandle || "").trim()) {
				setError("Aguarde o upload na Meta ou anexe o arquivo novamente.");
				return false;
			}
			if (headerPlaceholderIndexes.some((_, index) => !String(headerExamples[index] || "").trim())) {
				setError("Preencha o exemplo de cada variável do cabeçalho.");
				return false;
			}
			if (bodyPlaceholderIndexes.some((_, index) => !String(bodyExamples[index] || "").trim())) {
				setError("Preencha o exemplo de cada variável do corpo.");
				return false;
			}
			// Meta rejeita textos curtos demais com variáveis (Params Words Ratio Exceeds Limit)
			if (bodyPlaceholderIndexes.length > 0) {
				const bodyWords = String(newTemplate.body || "")
					.replace(/\{\{\d+\}\}/g, "")
					.trim()
					.split(/\s+/)
					.filter(Boolean).length;
				if (bodyWords < 3) {
					setError(
						"O corpo está curto demais para variáveis. A Meta exige mais texto fixo — aumente a mensagem (ex.: \"Olá {{1}}, tudo bem? Estamos testando o template.\")."
					);
					return false;
				}
			}
		}
		if (stepId === "buttons") {
			const invalid = buttons.some(btn => !String(btn.text || "").trim());
			if (invalid) {
				setError("Preencha o texto de todos os botões ou remova os vazios.");
				return false;
			}
			if (buttons.length > 3) {
				setError("No máximo 3 botões.");
				return false;
			}
			const invalidUrl = buttons.some(btn => {
				if (String(btn.type || "").toUpperCase() !== "URL") return false;
				return !String(btn.url || "").trim();
			});
			if (invalidUrl) {
				setError("Informe a URL dos botões do tipo Link.");
				return false;
			}
			const invalidUrlExample = buttons.some(btn => {
				if (String(btn.type || "").toUpperCase() !== "URL") return false;
				if (!listPlaceholderIndexes(btn.url).length) return false;
				return !String(btn.example || "").trim();
			});
			if (invalidUrlExample) {
				setError("Informe o exemplo da variável dinâmica da URL do botão.");
				return false;
			}
		}
		setError("");
		return true;
	};

	const goNext = () => {
		if (!validateStep(stepIndex)) return;
		setStepIndex(current => Math.min(current + 1, STEPS.length - 1));
	};

	const goBack = () => {
		setError("");
		setStepIndex(current => Math.max(current - 1, 0));
	};

	const handleAddTemplate = async () => {
		if (!validateStep(stepIndex)) return;
		if (!selectedWhatsApp) {
			setError("Selecione uma conexão.");
			return;
		}

		setLoading(true);
		setError("");

		try {
			if (!String(newTemplate.body || "").trim()) {
				setError("Informe o corpo do template.");
				setLoading(false);
				return;
			}

			const baseName =
				String(newTemplate.name || "").trim() ||
				`${process.env.REACT_APP_NOME || "template"}_${uuid()}`;

			const resolvedHeaderFormat = headerFormat === "NONE" ? "" : headerFormat;

			if (isHubConnection) {
				await apiV2.post("/hub/templates", {
					whatsappId: Number(selectedWhatsApp.id),
					name: baseName,
					category,
					headerFormat: resolvedHeaderFormat,
					header: headerFormat === "TEXT" ? newTemplate.header || "" : "",
					headerExample: isMediaHeader ? mediaHandle || "" : "",
					headerExamples: headerFormat === "TEXT" ? headerExamples : [],
					body: newTemplate.body || "",
					bodyExamples,
					footer: newTemplate.footer || "",
					buttons: cleanButtons,
				});
				handleClose();
				return;
			}

			const templateName = baseName;

			if (isMediaHeader) {
				const handle = await api.post("/WhatsAppMeta/generate-media-id", {
					mediaPath,
					selectedWhatsApp,
				});

				const components = buildTemplateComponents({
					headerFormat,
					header: newTemplate.header,
					body: newTemplate.body,
					footer: newTemplate.footer,
					mediaPath: handle.data,
					headerExamples,
					bodyExamples,
					buttons: cleanButtons,
				});

				const response = await fetch(
					`https://graph.facebook.com/v15.0/${selectedWhatsApp.wabaAccountId}/message_templates?name=${templateName
						.split("-")
						.join("_")
						.toLocaleLowerCase()}`,
					{
						method: "POST",
						headers: {
							Authorization: `Bearer ${selectedWhatsApp.wabaAccessToken}`,
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							name: templateName.split("-").join("_").toLocaleLowerCase(),
							language: "pt_BR",
							category,
							components,
						}),
					}
				);

				if (!response.ok) throw new Error("Falha ao criar template com mídia.");
				handleClose();
				return;
			}

			const components = buildTemplateComponents({
				headerFormat: resolvedHeaderFormat || "NONE",
				header: newTemplate.header,
				body: newTemplate.body,
				footer: newTemplate.footer,
				headerExamples,
				bodyExamples,
				buttons: cleanButtons,
			});

			await fetch(
				`https://graph.facebook.com/v15.0/${selectedWhatsApp.wabaAccountId}/message_templates?name=${templateName
					.split("-")
					.join("_")
					.toLocaleLowerCase()}`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${selectedWhatsApp.wabaAccessToken}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						language: "pt_BR",
						category,
						components,
					}),
				}
			);

			handleClose();
		} catch (err) {
			setError(err?.response?.data?.error || "Erro ao criar template.");
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	const handleUpdateTemplate = async () => {
		setLoading(true);
		setError("");

		try {
			await fetch(`https://graph.facebook.com/v15.0/${selectedTemplate.id}`, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${selectedTemplate.whatsApp.wabaAccessToken}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					language: "pt_BR",
					category: "UTILITY",
					components: buildTemplateComponents({
						headerFormat: headerFormat === "NONE" ? "TEXT" : headerFormat,
						header: newTemplate.header,
						body: newTemplate.body,
						footer: newTemplate.footer,
						mediaPath,
						headerExamples,
						bodyExamples,
						buttons: cleanButtons,
					}),
				}),
			});
			handleClose();
		} catch (err) {
			setError("Erro ao atualizar template.");
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	const isLastStep = stepIndex === STEPS.length - 1;
	const currentStepId = STEPS[stepIndex]?.id;

	const footer = (
		<div className="flex w-full flex-wrap items-center gap-2">
			<button type="button" className="btn btn-sub-gray" onClick={handleClose} disabled={loading}>
				{i18n.t("templates.templateModal.buttons.cancel")}
			</button>
			<div className="ml-auto flex items-center gap-2">
				{stepIndex > 0 && (
					<button type="button" className="btn btn-sub-gray" onClick={goBack} disabled={loading}>
						Voltar
					</button>
				)}
				{!isLastStep ? (
					<button type="button" className="btn bg-primary text-white" onClick={goNext} disabled={loading}>
						Continuar
					</button>
				) : (
					<button
						type="button"
						className="btn bg-primary text-white"
						onClick={isEditing ? handleUpdateTemplate : handleAddTemplate}
						disabled={loading}
					>
						{loading && (
							<span className="inline-block size-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
						)}
						{isEditing
							? i18n.t("templates.templateModal.buttons.update")
							: i18n.t("templates.templateModal.buttons.create")}
					</button>
				)}
			</div>
		</div>
	);

	return (
		<AppModal
			open={open}
			onClose={handleClose}
			title={
				isEditing
					? i18n.t("templates.templateModal.title.update")
					: i18n.t("templates.templateModal.title.create")
			}
			subtitle="Monte o template em etapas e acompanhe o preview em tempo real."
			maxWidth={1120}
			bodyClassName="!p-0"
			footer={footer}
		>
			<div className="grid min-h-[520px] grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
				<div className="flex min-w-0 flex-col border-b border-default-200 lg:border-b-0 lg:border-r dark:border-default-700">
					<div className="border-b border-default-200 px-5 py-4 dark:border-default-700">
						<StepRail
							steps={STEPS}
							activeIndex={stepIndex}
							onSelect={index => {
								if (index <= stepIndex) {
									setError("");
									setStepIndex(index);
								}
							}}
						/>
					</div>

					<div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
						{currentStepId === "setup" && (
							<>
								<div>
									<h3 className="m-0 text-base font-semibold text-default-900 dark:text-default-50">
										Configuração inicial
									</h3>
									<p className="mt-1 mb-0 text-sm text-default-500">
										Categoria define o uso do template (Marketing, Utilidade ou Autenticação).
									</p>
								</div>

								{!isEditing && (
									<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
										<div>
											<FieldLabel required>
												{i18n.t("templates.templateModal.connection")}
											</FieldLabel>
											<select
												className="form-select w-full"
												value={selectedWhatsAppId}
												onChange={event => setSelectedWhatsAppId(event.target.value)}
											>
												<option value="">Selecione...</option>
												{eligibleWhatsApps.map(whatsApp => (
													<option key={whatsApp.id} value={whatsApp.id}>
														{whatsApp.name}
														{whatsApp.hub ? " (Hub)" : ""}
													</option>
												))}
											</select>
											<FieldHint>
												{eligibleWhatsApps.length
													? "Somente conexões WhatsApp Business Account (Hub)."
													: "Nenhuma conexão WhatsApp Business Account encontrada."}
											</FieldHint>
										</div>

										<div>
											<FieldLabel required>Categoria</FieldLabel>
											<select
												className="form-select w-full"
												value={category}
												onChange={event => setCategory(event.target.value)}
											>
												{CATEGORY_OPTIONS.map(option => (
													<option key={option.value} value={option.value}>
														{option.label}
													</option>
												))}
											</select>
											<FieldHint>Idioma fixo: pt_BR</FieldHint>
										</div>
									</div>
								)}

								<div>
									<FieldLabel required>{i18n.t("templates.templateModal.name")}</FieldLabel>
									<input
										type="text"
										className="form-input w-full"
										value={newTemplate.name}
										onChange={event =>
											setNewTemplate(current => ({
												...current,
												name: event.target.value.replace(/\s+/g, "_").toLowerCase(),
											}))
										}
										placeholder="ex: boas_vindas_cliente"
									/>
									<FieldHint>Use letras minúsculas, números e underscore.</FieldHint>
								</div>
							</>
						)}

						{currentStepId === "content" && (
							<>
								<div>
									<h3 className="m-0 text-base font-semibold text-default-900 dark:text-default-50">
										Conteúdo da mensagem
									</h3>
									<p className="mt-1 mb-0 text-sm text-default-500">
										O cabeçalho pode ser texto, imagem, vídeo ou documento — em qualquer categoria.
									</p>
								</div>

								<div>
									<FieldLabel>Tipo do cabeçalho</FieldLabel>
									<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
										{HEADER_FORMAT_OPTIONS.map(option => {
											const active = headerFormat === option.value;
											return (
												<button
													key={option.value}
													type="button"
													onClick={() => {
														setHeaderFormat(option.value);
														if (option.value === "NONE" || option.value === "TEXT") {
															setMediaPath(null);
															setMediaHandle("");
														}
														if (option.value !== "TEXT") {
															setNewTemplate(current => ({ ...current, header: "" }));
														}
													}}
													className={[
														"rounded-xl border px-3 py-2.5 text-sm font-medium transition",
														active
															? "border-primary bg-primary/10 text-primary"
															: "border-default-200 bg-default-50 text-default-700 hover:border-default-300 dark:border-default-700 dark:bg-default-900 dark:text-default-200",
													].join(" ")}
												>
													{option.label}
												</button>
											);
										})}
									</div>
								</div>

								{headerFormat === "TEXT" && (
									<div>
										<div className="mb-2 flex items-center justify-between gap-2">
											<FieldLabel required>
												{i18n.t("templates.templateModal.header")}
											</FieldLabel>
											<button
												type="button"
												className="btn btn-sub-gray !px-2 !py-1 text-xs"
												onClick={() => insertVariable("header")}
											>
												+ variável
											</button>
										</div>
										<input
											type="text"
											className="form-input w-full"
											maxLength={60}
											value={newTemplate.header}
											onChange={event =>
												setNewTemplate(current => ({
													...current,
													header: event.target.value,
												}))
											}
											placeholder="Bem vindo, {{1}}!"
										/>
										<FieldHint>{`${newTemplate.header?.length || 0}/60`}</FieldHint>
									</div>
								)}

								{isMediaHeader && (
									<div>
										<FieldLabel required>
											Arquivo de exemplo (
											{HEADER_FORMAT_OPTIONS.find(o => o.value === headerFormat)?.label})
										</FieldLabel>
										<div className="rounded-xl border border-default-200 bg-default-50 p-3 dark:border-default-700 dark:bg-default-900">
											<div className="flex flex-wrap items-center gap-3">
												<label
													className={[
														"btn btn-sub-gray cursor-pointer",
														uploadingMedia || !selectedWhatsApp ? "pointer-events-none opacity-60" : "",
													].join(" ")}
												>
													{uploadingMedia ? (
														<span className="inline-block size-4 animate-spin rounded-full border-2 border-default-400/30 border-t-default-700" />
													) : (
														<IconPaperclip />
													)}
													{uploadingMedia ? "Enviando para Meta..." : "Anexar arquivo"}
													<input
														type="file"
														className="hidden"
														accept={MEDIA_ACCEPT[headerFormat]}
														disabled={uploadingMedia || !selectedWhatsApp}
														onChange={handleUpdateMedia}
													/>
												</label>
												{mediaPath && (
													<button
														type="button"
														className="btn border border-danger/20 bg-danger/10 text-danger hover:bg-danger/15"
														onClick={() => {
															setMediaPath(null);
															setMediaHandle("");
														}}
														disabled={uploadingMedia}
													>
														Remover
													</button>
												)}
											</div>
											<FieldHint>
												{!selectedWhatsApp
													? "Selecione a conexão na etapa anterior antes de anexar."
													: uploadingMedia
														? "Fazendo upload no Hub/Meta..."
														: mediaHandle
															? `Handle Meta pronto: ${String(mediaHandle).slice(0, 28)}…`
															: mediaPath
																? mediaPath
																: isHubConnection
																	? "A Meta não aceita URL nem SVG. Use JPEG/PNG, MP4 ou PDF — o arquivo sobe no Hub e o handle é usado na criação."
																	: "A Meta exige um exemplo de mídia para aprovar o template."}
											</FieldHint>
										</div>
									</div>
								)}

								<div>
									<div className="mb-2 flex items-center justify-between gap-2">
										<FieldLabel required>
											{i18n.t("templates.templateModal.body")}
										</FieldLabel>
										<button
											type="button"
											className="btn btn-sub-gray !px-2 !py-1 text-xs"
											onClick={() => insertVariable("body")}
										>
											+ variável
										</button>
									</div>
									<textarea
										className="form-input min-h-[160px] w-full resize-y"
										maxLength={1024}
										value={newTemplate.body}
										onChange={event =>
											setNewTemplate(current => ({
												...current,
												body: event.target.value,
											}))
										}
										placeholder="Olá! Criando um {{1}} pela API."
									/>
									<FieldHint>{`${newTemplate.body?.length || 0}/1024`}</FieldHint>
								</div>

								<div>
									<FieldLabel>{i18n.t("templates.templateModal.footer")}</FieldLabel>
									<input
										type="text"
										className="form-input w-full"
										maxLength={60}
										value={newTemplate.footer}
										onChange={event =>
											setNewTemplate(current => ({
												...current,
												footer: event.target.value,
											}))
										}
										placeholder="Use os botões para teste."
									/>
									<FieldHint>{`${newTemplate.footer?.length || 0}/60`}</FieldHint>
								</div>

								{(headerPlaceholderIndexes.length > 0 || bodyPlaceholderIndexes.length > 0) && (
									<div className="rounded-xl border border-amber-200/80 bg-amber-50/70 p-4 dark:border-amber-900/50 dark:bg-amber-950/30">
										<h4 className="m-0 text-sm font-semibold text-default-900 dark:text-default-100">
											Exemplos das variáveis
										</h4>
										<p className="mt-1 mb-4 text-xs text-default-500">
											A Meta exige um valor de exemplo para cada {"{{n}}"} usado no template.
										</p>

										{headerPlaceholderIndexes.length > 0 && (
											<div className="mb-4 space-y-3">
												<p className="m-0 text-xs font-semibold uppercase tracking-wide text-default-500">
													Cabeçalho
												</p>
												{headerPlaceholderIndexes.map((num, index) => (
													<div key={`header-ex-${num}`}>
														<FieldLabel required>{`{{${num}}}`}</FieldLabel>
														<input
															type="text"
															className="form-input w-full"
															value={headerExamples[index] || ""}
															onChange={event =>
																setHeaderExamples(current => {
																	const next = [...current];
																	next[index] = event.target.value;
																	return next;
																})
															}
															placeholder={num === 1 ? "John" : `exemplo_${num}`}
														/>
													</div>
												))}
											</div>
										)}

										{bodyPlaceholderIndexes.length > 0 && (
											<div className="space-y-3">
												<p className="m-0 text-xs font-semibold uppercase tracking-wide text-default-500">
													Corpo
												</p>
												{bodyPlaceholderIndexes.map((num, index) => (
													<div key={`body-ex-${num}`}>
														<FieldLabel required>{`{{${num}}}`}</FieldLabel>
														<input
															type="text"
															className="form-input w-full"
															value={bodyExamples[index] || ""}
															onChange={event =>
																setBodyExamples(current => {
																	const next = [...current];
																	next[index] = event.target.value;
																	return next;
																})
															}
															placeholder={num === 1 ? "template" : `exemplo_${num}`}
														/>
													</div>
												))}
											</div>
										)}
									</div>
								)}
							</>
						)}

						{currentStepId === "buttons" && (
							<>
								<div>
									<h3 className="m-0 text-base font-semibold text-default-900 dark:text-default-50">
										Botões (opcional)
									</h3>
									<p className="mt-1 mb-0 text-sm text-default-500">
										Até 3 botões. Resposta rápida ou link. URL com {"{{1}}"} exige exemplo.
									</p>
								</div>

								<div className="space-y-3">
									{buttons.map((btn, index) => {
										const isUrl = String(btn.type || "").toUpperCase() === "URL";
										const urlHasVar = isUrl && listPlaceholderIndexes(btn.url).length > 0;
										return (
											<div
												key={btn.id}
												className="space-y-3 rounded-xl border border-default-200 bg-default-50 p-3 dark:border-default-700 dark:bg-default-900"
											>
												<div className="flex items-end gap-2">
													<div className="w-36 shrink-0">
														<FieldLabel>Tipo</FieldLabel>
														<select
															className="form-select w-full"
															value={btn.type || "QUICK_REPLY"}
															onChange={event =>
																setButtons(current =>
																	current.map(item =>
																		item.id === btn.id
																			? {
																					...item,
																					type: event.target.value,
																					url: event.target.value === "URL" ? item.url || "" : "",
																					example:
																						event.target.value === "URL"
																							? item.example || ""
																							: "",
																				}
																			: item
																	)
																)
															}
														>
															<option value="QUICK_REPLY">Resposta rápida</option>
															<option value="URL">Link (URL)</option>
														</select>
													</div>
													<div className="min-w-0 flex-1">
														<FieldLabel>{`Botão ${index + 1}`}</FieldLabel>
														<input
															type="text"
															className="form-input w-full"
															maxLength={25}
															value={btn.text}
															onChange={event =>
																setButtons(current =>
																	current.map(item =>
																		item.id === btn.id
																			? { ...item, text: event.target.value }
																			: item
																	)
																)
															}
															placeholder="Ex: Botão 1"
														/>
													</div>
													<button
														type="button"
														className="btn btn-sub-gray !p-2 text-danger"
														onClick={() =>
															setButtons(current =>
																current.filter(item => item.id !== btn.id)
															)
														}
														aria-label="Remover botão"
													>
														<IconTrash />
													</button>
												</div>

												{isUrl && (
													<div>
														<FieldLabel required>URL</FieldLabel>
														<input
															type="text"
															className="form-input w-full"
															value={btn.url || ""}
															onChange={event =>
																setButtons(current =>
																	current.map(item =>
																		item.id === btn.id
																			? { ...item, url: event.target.value }
																			: item
																	)
																)
															}
															placeholder="https://site.com/{{1}}"
														/>
														<FieldHint>
															Use {"{{1}}"} no final da URL se for dinâmica.
														</FieldHint>
													</div>
												)}

												{urlHasVar && (
													<div>
														<FieldLabel required>Exemplo da variável da URL</FieldLabel>
														<input
															type="text"
															className="form-input w-full"
															value={btn.example || ""}
															onChange={event =>
																setButtons(current =>
																	current.map(item =>
																		item.id === btn.id
																			? { ...item, example: event.target.value }
																			: item
																	)
																)
															}
															placeholder="promo"
														/>
													</div>
												)}
											</div>
										);
									})}
								</div>

								{buttons.length < 3 && (
									<button
										type="button"
										className="btn btn-sub-gray"
										onClick={() =>
											setButtons(current => [
												...current,
												{ id: uuid(), type: "QUICK_REPLY", text: "", url: "", example: "" },
											])
										}
									>
										<IconPlus />
										Adicionar botão
									</button>
								)}
							</>
						)}

						{currentStepId === "review" && (
							<>
								<div>
									<h3 className="m-0 text-base font-semibold text-default-900 dark:text-default-50">
										Revisar e enviar
									</h3>
									<p className="mt-1 mb-0 text-sm text-default-500">
										Confira o resumo. O preview ao lado mostra como a mensagem aparece.
									</p>
								</div>

								<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
									<div className="rounded-xl border border-default-200 bg-default-50 p-3 dark:border-default-700 dark:bg-default-900">
										<p className="m-0 text-xs uppercase tracking-wide text-default-500">Nome</p>
										<p className="mb-0 mt-1 text-sm font-medium text-default-900 dark:text-default-100">
											{newTemplate.name || "—"}
										</p>
									</div>
									<div className="rounded-xl border border-default-200 bg-default-50 p-3 dark:border-default-700 dark:bg-default-900">
										<p className="m-0 text-xs uppercase tracking-wide text-default-500">
											Categoria
										</p>
										<p className="mb-0 mt-1 text-sm font-medium text-default-900 dark:text-default-100">
											{CATEGORY_OPTIONS.find(o => o.value === category)?.label || category}
										</p>
									</div>
									<div className="rounded-xl border border-default-200 bg-default-50 p-3 dark:border-default-700 dark:bg-default-900">
										<p className="m-0 text-xs uppercase tracking-wide text-default-500">
											Cabeçalho
										</p>
										<p className="mb-0 mt-1 text-sm font-medium text-default-900 dark:text-default-100">
											{HEADER_FORMAT_OPTIONS.find(o => o.value === headerFormat)?.label ||
												headerFormat}
										</p>
									</div>
									<div className="rounded-xl border border-default-200 bg-default-50 p-3 dark:border-default-700 dark:bg-default-900">
										<p className="m-0 text-xs uppercase tracking-wide text-default-500">
											Conexão
										</p>
										<p className="mb-0 mt-1 text-sm font-medium text-default-900 dark:text-default-100">
											{selectedWhatsApp?.name || "—"}
											{isHubConnection ? " · Hub" : ""}
										</p>
									</div>
								</div>

								<div className="rounded-xl border border-default-200 bg-default-50 p-3 dark:border-default-700 dark:bg-default-900">
									<p className="m-0 text-xs uppercase tracking-wide text-default-500">Corpo</p>
									<p className="mb-0 mt-1 whitespace-pre-wrap text-sm text-default-800 dark:text-default-200">
										{newTemplate.body || "—"}
									</p>
								</div>
							</>
						)}

						<FieldError>{error}</FieldError>
					</div>
				</div>

				<aside className="flex flex-col bg-[#f0f2f5] p-4 dark:bg-default-950">
					<p className="mb-3 mt-0 text-xs font-semibold uppercase tracking-wide text-default-500">
						Pré-visualização
					</p>
					<div className="min-h-0 flex-1">
						<TemplateConversationPreview
							template={previewTemplate}
							params={previewParams}
							mediaPath={mediaPath}
							darkMode={darkMode}
						/>
					</div>
					<p className="mb-0 mt-3 text-center text-[11px] text-default-500">
						Atualiza em tempo real conforme você edita
					</p>
				</aside>
			</div>
		</AppModal>
	);
}
